import React, { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { CloseIcon } from '../../assets/icons/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { Flag, FlagCode } from '../../components/flag/index';
import { Input } from '../../components/input/index';
import Loader from '../../components/loader/index';
import { DeprecatedModalWrapper } from '../../components/modal/index';
import { Tooltip } from '../../components/tooltip/index';
import { Company, useFormat, useLocale, useNavigate, User } from '../../hooks/index';
import { inviteStakeholderStore } from '../../storage/local/index';
import { stakeholderTypeTranslation } from '../../translations/stakeholder-translation.util';
import { PageRoute } from '../../types/page.types';
import {
  accessRoleTranslationKey,
  b2bStakeholderTypes,
  b2cStakeholderTypes,
} from '../../types/stakeholder.types';

const InviteModal: FC = () => {
  const { format } = useFormat();
  const invitedStakeholder = inviteStakeholderStore.get();
  const [isOpenModal, setOpen] = useState<boolean>(!!invitedStakeholder || false);
  const { messagesLocale } = useLocale();
  const { selectedCompany, isLoading } = Company.useSelected();
  const { user, isLoading: isLoadingUser } = User.useUser();
  const navigate = useNavigate(PageRoute.MY_HOLDINGS);

  const handleClose = () => {
    setOpen(false);
    inviteStakeholderStore.delete();
    navigate();
  };
  if ((isOpenModal && (!selectedCompany || isLoading)) || isLoadingUser) return <Loader />;

  const stakeholder = selectedCompany?.stakeholder;
  const stakeholderTypeDisplayValue =
    stakeholder?.accessRole != null
      ? messagesLocale[accessRoleTranslationKey[stakeholder.accessRole]]
      : stakeholder?.type
        ? messagesLocale[stakeholderTypeTranslation[stakeholder.type]]
        : '-';

  const birthDayDate = stakeholder?.birthday ? new Date(stakeholder.birthday) : null;
  const passportExpDate = stakeholder?.passportExpDate
    ? new Date(stakeholder.passportExpDate)
    : null;
  const incDate = stakeholder?.incDate ? new Date(stakeholder.incDate) : null;

  const formattedBirthDate = birthDayDate ? format(birthDayDate) : '-';
  const formattedPassportExpDate = passportExpDate ? format(passportExpDate) : '-';
  const formattedIncDate = incDate ? format(incDate) : '-';

  const isNotGuest = !stakeholder?.accessRole;
  const isSubEmail = user ? user?.email !== stakeholder?.email : true;

  return (
    <DeprecatedModalWrapper isOpen={isOpenModal} mode="BLUR">
      <div className="relative flex w-full max-w-[600px] flex-col gap-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <Button
          className="absolute right-4 top-4 flex size-8 items-center justify-center rounded-full shadow-sm"
          onClick={handleClose}
          styleType="NONE"
        >
          <CloseIcon />
        </Button>
        <div className="flex flex-col gap-4 overflow-hidden text-center">
          <span
            className={twMerge(
              'flex justify-center truncate !bg-clip-text pr-4 text-4xl-mobile font-bold text-transparent lg:text-4xl',
              isSubEmail && 'justify-center',
            )}
            style={{
              background: 'linear-gradient(132.59deg, #475467 29.58%, #101828 84.27%)',
            }}
          >
            <AppFormattedMessage id="WELCOME" />
            <Tooltip content={stakeholder?.fullName}>
              <span>
                {stakeholder?.type && !b2cStakeholderTypes.includes(stakeholder?.type)
                  ? ', ' + stakeholder?.fullName
                  : '!'}
              </span>
            </Tooltip>
          </span>
          <span className="text-xs font-medium text-gray-600">
            <AppFormattedMessage id="BEFORE_YOU_START_YOUR_JOURNEY_CONFIRMATION" />
          </span>
        </div>
        <div className="flex w-full flex-col gap-4">
          <div className="flex gap-4">
            {stakeholder?.type && b2bStakeholderTypes.includes(stakeholder?.type) && (
              <>
                <div className={twMerge('flex w-full flex-col gap-4')}>
                  <Input
                    disabled
                    onChange={() => ''}
                    placeholder={<AppFormattedMessage id="FULL_NAME" />}
                    value={stakeholder?.fullName || '-'}
                    wrapperClassName="w-full"
                  />
                  <Input
                    disabled
                    onChange={() => ''}
                    placeholder={<AppFormattedMessage id="STAKEHOLDER_TYPE" />}
                    value={stakeholderTypeDisplayValue}
                    wrapperClassName="w-full"
                  />
                  {isNotGuest && !isSubEmail && (
                    <>
                      <Input
                        disabled
                        onChange={() => ''}
                        placeholder={<AppFormattedMessage id="PASSPORT_NO" />}
                        value={stakeholder.passportNo || '-'}
                        wrapperClassName="w-full"
                      />
                      <Input
                        disabled
                        onChange={() => ''}
                        placeholder={<AppFormattedMessage id="BIRTHDAY" />}
                        value={formattedBirthDate}
                        wrapperClassName="w-full"
                      />
                    </>
                  )}
                </div>
                <div className="flex w-full flex-col gap-4">
                  <Input
                    disabled
                    onChange={() => ''}
                    placeholder={<AppFormattedMessage id="EMAIL" />}
                    value={user?.email || '-'}
                    wrapperClassName="w-full"
                  />
                  {isNotGuest && !isSubEmail && (
                    <>
                      <Input
                        disabled
                        iconBeforeInput={
                          <Flag
                            className="mt-5"
                            countryCode={
                              stakeholder.nationalityCountry?.iso2.toUpperCase() as FlagCode
                            }
                          />
                        }
                        onChange={() => ''}
                        placeholder={<AppFormattedMessage id="NATIONALITY" />}
                        value={stakeholder?.nationalityCountry?.name || '-'}
                        wrapperClassName="w-full"
                      />
                      <Input
                        disabled
                        onChange={() => ''}
                        placeholder={<AppFormattedMessage id="PASSPORT_EXPIRY_DATE" />}
                        value={formattedPassportExpDate}
                        wrapperClassName="w-full"
                      />
                      <Input
                        disabled
                        onChange={() => ''}
                        placeholder={<AppFormattedMessage id="PHONE_NUMBER" />}
                        value={stakeholder?.phone || '-'}
                        wrapperClassName="w-full"
                      />
                    </>
                  )}
                </div>
              </>
            )}

            {stakeholder?.type && b2cStakeholderTypes.includes(stakeholder.type) && (
              <>
                {isSubEmail ? (
                  <div className="flex w-full flex-col gap-4">
                    <Input
                      disabled
                      onChange={() => ''}
                      placeholder={<AppFormattedMessage id="FULL_NAME" />}
                      value={stakeholder?.fullName || '-'}
                      wrapperClassName="w-full"
                    />
                    <Input
                      disabled
                      onChange={() => ''}
                      placeholder={<AppFormattedMessage id="STAKEHOLDER_TYPE" />}
                      value={stakeholderTypeDisplayValue}
                      wrapperClassName="w-full"
                    />
                    <Input
                      disabled
                      onChange={() => ''}
                      placeholder={<AppFormattedMessage id="EMAIL" />}
                      value={user?.email || '-'}
                      wrapperClassName="w-full"
                    />
                  </div>
                ) : (
                  <div className="flex w-full flex-col gap-4">
                    <Input
                      disabled
                      onChange={() => ''}
                      placeholder={<AppFormattedMessage id="FULL_NAME" />}
                      value={stakeholder?.fullName || '-'}
                      wrapperClassName="w-full"
                    />
                    <Input
                      disabled
                      onChange={() => ''}
                      placeholder={<AppFormattedMessage id="STAKEHOLDER_TYPE" />}
                      value={stakeholderTypeDisplayValue}
                      wrapperClassName="w-full"
                    />
                    {isNotGuest && !isSubEmail && (
                      <Input
                        disabled
                        onChange={() => ''}
                        placeholder={<AppFormattedMessage id="COMPANY_IDENTIFIER" />}
                        value={stakeholder?.stakeholderCompanyId || '-'}
                        wrapperClassName="w-full"
                      />
                    )}
                  </div>
                )}

                {!isSubEmail && (
                  <div className="flex w-full flex-col gap-4">
                    <Input
                      disabled
                      onChange={() => ''}
                      placeholder={<AppFormattedMessage id="EMAIL" />}
                      value={user?.email || '-'}
                      wrapperClassName="w-full"
                    />
                    {isNotGuest && (
                      <>
                        <Input
                          disabled
                          iconBeforeInput={
                            <Flag
                              className="mt-5"
                              countryCode={stakeholder.countryOfInc?.iso2.toUpperCase() as FlagCode}
                            />
                          }
                          onChange={() => ''}
                          placeholder={<AppFormattedMessage id="COUNTRY_OF_COMPANY" />}
                          value={stakeholder?.countryOfInc?.name || '-'}
                          wrapperClassName="w-full"
                        />
                        <Input
                          disabled
                          onChange={() => ''}
                          placeholder={<AppFormattedMessage id="DATE_OF_INCORPORATION" />}
                          value={formattedIncDate}
                          wrapperClassName="w-full"
                        />
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          {stakeholder?.type &&
            b2cStakeholderTypes.includes(stakeholder.type) &&
            isNotGuest &&
            !isSubEmail && (
              <>
                <Input
                  disabled
                  onChange={() => ''}
                  placeholder={<AppFormattedMessage id="PHONE_NUMBER" />}
                  value={stakeholder?.phone || '-'}
                  wrapperClassName="w-full"
                />
                <Input
                  disabled
                  onChange={() => ''}
                  placeholder={<AppFormattedMessage id="ADDRESS" />}
                  value={stakeholder?.address || '-'}
                  wrapperClassName="w-full"
                />
              </>
            )}
        </div>
        <Button className="w-full" onClick={handleClose}>
          <AppFormattedMessage id="I_HAVE_SEEN_MY_INFO" />
        </Button>
      </div>
    </DeprecatedModalWrapper>
  );
};

export default InviteModal;
